import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Paper, 
  Typography, 
  Tabs, 
  Tab, 
  Divider, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemIcon, 
  Button, 
  Card, 
  CardContent, 
  CardMedia, 
  Avatar, 
  Badge, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  LinearProgress,
  useMediaQuery,
  useTheme,
  SwipeableDrawer,
  Fab,
  Tooltip,
  AppBar,
  Toolbar
} from '@mui/material';
import { 
  Person as PersonIcon,
  ShoppingBag as ShoppingBagIcon,
  Favorite as FavoriteIcon,
  LocationOn as LocationIcon,
  CreditCard as CreditCardIcon,
  History as HistoryIcon,
  Store as StoreIcon,
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
  LocalShipping as LocalShippingIcon,
  Star as StarIcon,
  MoreVert as MoreVertIcon,
  SupportAgent as SupportAgentIcon,
  Menu as MenuIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import customerDashboardBn from '../../i18n/bn/customerDashboard';
import customerDashboardEn from '../../i18n/en/customerDashboard';
import customerDashboardNp from '../../i18n/np/customerDashboard';
import customerDashboardPk from '../../i18n/pk/customerDashboard';
import customerDashboardBt from '../../i18n/bt/customerDashboard';
import customerDashboardDe from '../../i18n/de/customerDashboard';
import customerDashboardSl from '../../i18n/sl/customerDashboard';
import customerDashboardAf from '../../i18n/af/customerDashboard';
import customerDashboardCn from '../../i18n/cn/customerDashboard';

// Styled Components for Beautiful Design
const VideoBackgroundBox = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderRadius: '4px',
  color: 'white',
  overflow: 'hidden',
  minHeight: '400px',
  display: 'flex',
  alignItems: 'center',
  '& video': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    zIndex: 1,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.8) 0%, rgba(118, 75, 162, 0.8) 100%)',
    zIndex: 2,
  }
}));

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: '4px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
  }
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: '4px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
}));

const MetricCard = styled(Card)(({ gradient }) => ({
  background: gradient,
  color: 'white',
  borderRadius: '4px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  transition: 'all 0.3s ease',
  border: 'none',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.3)',
  }
}));

const StyledListItem = styled(ListItem)(({ theme, selected }) => ({
  borderRadius: '4px',
  margin: '4px 8px',
  backgroundColor: selected ? 'rgba(102, 126, 234, 0.1)' : 'transparent',
  border: selected ? '2px solid rgba(102, 126, 234, 0.2)' : '2px solid transparent',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(102, 126, 234, 0.05)',
    transform: 'translateX(4px)',
  }
}));

const CustomerDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const lang = (typeof window !== 'undefined' && localStorage.getItem('preferredLanguage')) || 'bn';
  const t = lang === 'en' ? customerDashboardEn : lang === 'np' ? customerDashboardNp : lang === 'pk' ? customerDashboardPk : lang === 'bt' ? customerDashboardBt : lang === 'de' ? customerDashboardDe : lang === 'sl' ? customerDashboardSl : lang === 'af' ? customerDashboardAf : lang === 'cn' ? customerDashboardCn : customerDashboardBn;
  const [tabValue, setTabValue] = useState(0);
  const [openAddressDialog, setOpenAddressDialog] = useState(false);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [openOrderDialog, setOpenOrderDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [newAddress, setNewAddress] = useState({
    type: 'Home',
    address: '',
    phone: ''
  });
  const [newPayment, setNewPayment] = useState({
    type: 'Credit Card',
    number: '',
    expiry: ''
  });

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setOpenOrderDialog(true);
  };

  // Enhanced Mock data with more details
  const userStats = {
    totalOrders: 45,
    totalSpent: 15680,
    memberSince: 'মার্চ ২০২৪',
    loyaltyPoints: 1250,
    nextReward: 1500
  };

  const recentOrders = [
    { id: 1, date: '২৫ মার্চ, ২০২৫', store: 'তাজা সবজি দোকান', status: 'Delivered', amount: 250, items: 5, placedBySupport: false },
    { id: 2, date: '২০ মার্চ, ২০২৫', store: 'মাংস প্যারাডাইজ', status: 'Processing', amount: 450, items: 3, placedBySupport: true, supportAgent: 'সাকিব হাসান' },
    { id: 3, date: '১৫ মার্চ, ২০২৫', store: 'ফলের রাজ্য', status: 'Shipped', amount: 320, items: 7, placedBySupport: true, supportAgent: 'নাসিরা বেগম' },
  ];

  const wishlistItems = [
    { id: 1, name: 'জৈব টমেটো', price: 120, store: 'তাজা সবজি দোকান', image: '/images/groceries/tomato.webp', rating: 4.8 },
    { id: 2, name: 'প্রিমিয়াম গরুর মাংস', price: 580, store: 'মাংস প্যারাডাইজ', image: '/images/groceries/beef.jpg', rating: 4.9 },
    { id: 3, name: 'মৌসুমি আম', price: 350, store: 'ফলের রাজ্য', image: '/images/groceries/am.jpeg', rating: 4.7 },
  ];

  const addresses = [
    { id: 1, type: 'Home', address: '১২৩ মেইন স্ট্রিট, ঢাকা, বাংলাদেশ', phone: '+880 1234 567890', isDefault: true },
    { id: 2, type: 'Office', address: '৪৫৬ ব্যবসায়িক এভিনিউ, ঢাকা, বাংলাদেশ', phone: '+880 9876 543210', isDefault: false },
  ];

  const paymentMethods = [
    { id: 1, type: 'Credit Card', number: '**** **** **** ১২৩৪', expiry: '১২/২৬', isDefault: true },
    { id: 2, type: 'Bkash', number: '**** **** ৫৬৭৮', expiry: 'N/A', isDefault: false },
  ];

  const handleAddressDialogOpen = () => {
    setOpenAddressDialog(true);
  };

  const handleAddressDialogClose = () => {
    setOpenAddressDialog(false);
    setNewAddress({
      type: 'Home',
      address: '',
      phone: ''
    });
  };

  const handlePaymentDialogOpen = () => {
    setOpenPaymentDialog(true);
  };

  const handlePaymentDialogClose = () => {
    setOpenPaymentDialog(false);
    setNewPayment({
      type: 'Credit Card',
      number: '',
      expiry: ''
    });
  };

  const handleAddressSubmit = () => {
    console.log('New address:', newAddress);
    handleAddressDialogClose();
  };

  const handlePaymentSubmit = () => {
    console.log('New payment:', newPayment);
    handlePaymentDialogClose();
  };

  const handleMobileDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const handleMobileDrawerClose = () => {
    setMobileDrawerOpen(false);
  };

  const handleMobileTabChange = (newValue) => {
    setTabValue(newValue);
    setMobileDrawerOpen(false);
  };

  return (
    <>
      <Header />
      
      {/* Mobile Navigation Bar */}
      {isMobile && (
        <AppBar 
          position="sticky" 
          sx={{ 
            top: 0, 
            zIndex: 1100,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleMobileDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
              {t.sidebar.title}
            </Typography>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.2)' }}>
              <PersonIcon />
            </Avatar>
          </Toolbar>
        </AppBar>
      )}
      
      {/* Hero Section with Video Background */}
      <VideoBackgroundBox sx={{ py: { xs: 4, md: 6 }, mb: 4 }}>
        <video autoPlay muted loop>
          <source src="/videos/default.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 3 }}>
          <Grid container spacing={{ xs: 2, md: 4 }} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography 
                variant={isMobile ? "h4" : "h3"}
                sx={{ 
                  fontWeight: 700, 
                  mb: 2,
                  background: 'linear-gradient(45deg, #ffffff 30%, #f8f9fa 90%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textAlign: { xs: 'center', md: 'left' }
                }}
              >
                {t.hero.welcome('মাহমুদুর রহমান')}
              </Typography>
              <Typography 
                variant={isMobile ? "body1" : "h6"} 
                sx={{ 
                  opacity: 0.9, 
                  mb: 3,
                  textAlign: { xs: 'center', md: 'left' }
                }}
              >
                {t.hero.subtitle}
              </Typography>
              
              {/* Quick Stats */}
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700 }}>
                      {userStats.totalOrders}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {t.stats.totalOrders}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700 }}>
                      ৳{userStats.totalSpent.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {t.stats.totalSpent}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700 }}>
                      {userStats.loyaltyPoints}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {t.stats.points}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ opacity: 0.8, mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {t.stats.nextReward}
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={(userStats.loyaltyPoints / userStats.nextReward) * 100}
                      sx={{ 
                        height: 6, 
                        borderRadius: 4,
                        backgroundColor: 'rgba(255,255,255,0.3)',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: '#fff',
                        }
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Avatar 
                  sx={{ 
                    width: { xs: 80, sm: 100, md: 120 }, 
                    height: { xs: 80, sm: 100, md: 120 }, 
                    mx: 'auto', 
                    mb: 2,
                    border: '4px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                  }}
                >
                  <PersonIcon sx={{ fontSize: { xs: 40, sm: 50, md: 70 } }} />
                </Avatar>
                <Typography variant={isMobile ? "body1" : "h6"} sx={{ mb: 1 }}>
                  01722611185
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  {userStats.memberSince} {t.labels.memberSinceSuffix}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </VideoBackgroundBox>

      {/* Mobile Navigation Drawer */}
      <SwipeableDrawer
        anchor="left"
        open={mobileDrawerOpen}
        onClose={handleMobileDrawerClose}
        onOpen={() => setMobileDrawerOpen(true)}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            backgroundColor: 'white',
          },
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {t.sidebar.title}
            </Typography>
            <IconButton onClick={handleMobileDrawerClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        
        <List component="nav" sx={{ p: 1 }}>
          <StyledListItem 
            button 
            selected={tabValue === 0}
            onClick={() => handleMobileTabChange(0)}
          >
            <ListItemIcon>
              <ShoppingBagIcon color={tabValue === 0 ? "primary" : "inherit"} />
            </ListItemIcon>
            <ListItemText 
              primary={t.sidebar.orders} 
              primaryTypographyProps={{ 
                fontWeight: tabValue === 0 ? 600 : 400 
              }}
            />
            <Badge badgeContent={recentOrders.length} color="primary" />
          </StyledListItem>
          
          <StyledListItem 
            button 
            selected={tabValue === 1}
            onClick={() => handleMobileTabChange(1)}
          >
            <ListItemIcon>
              <FavoriteIcon color={tabValue === 1 ? "primary" : "inherit"} />
            </ListItemIcon>
            <ListItemText 
              primary={t.sidebar.wishlist} 
              primaryTypographyProps={{ 
                fontWeight: tabValue === 1 ? 600 : 400 
              }}
            />
            <Badge badgeContent={wishlistItems.length} color="primary" />
          </StyledListItem>
          
          <StyledListItem 
            button 
            selected={tabValue === 2}
            onClick={() => handleMobileTabChange(2)}
          >
            <ListItemIcon>
              <LocationIcon color={tabValue === 2 ? "primary" : "inherit"} />
            </ListItemIcon>
            <ListItemText 
              primary={t.sidebar.addresses} 
              primaryTypographyProps={{ 
                fontWeight: tabValue === 2 ? 600 : 400 
              }}
            />
          </StyledListItem>
          
          <StyledListItem 
            button 
            selected={tabValue === 3}
            onClick={() => handleMobileTabChange(3)}
          >
            <ListItemIcon>
              <CreditCardIcon color={tabValue === 3 ? "primary" : "inherit"} />
            </ListItemIcon>
            <ListItemText 
              primary={t.sidebar.payments} 
              primaryTypographyProps={{ 
                fontWeight: tabValue === 2 ? 600 : 400 
              }}
            />
          </StyledListItem>
        </List>
        
        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ p: 2 }}>
          <Button 
            variant="contained" 
            fullWidth 
            sx={{ 
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
              borderRadius: '4px',
              py: 1.5,
              fontWeight: 600
            }}
            component={Link}
            to="/customer/dashboard/profile/edit"
            onClick={handleMobileDrawerClose}
          >
            {t.sidebar.editProfile}
          </Button>
        </Box>
      </SwipeableDrawer>

      {/* Mobile Floating Action Button */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="menu"
          onClick={handleMobileDrawerToggle}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1000,
            display: { xs: 'flex', md: 'none' },
            background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)'
          }}
        >
          <MenuIcon />
        </Fab>
      )}

      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Grid container spacing={4}>
          {/* Sidebar - Hidden on Mobile */}
          {!isMobile && (
            <Grid item xs={12} md={3}>
            <StyledPaper sx={{ p: 0, overflow: 'hidden' }}>
              <Box sx={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                p: 3,
                textAlign: 'center'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t.sidebar.title}
                </Typography>
              </Box>
              
              <List component="nav" sx={{ p: 1 }}>
                <StyledListItem 
                  button 
                  selected={tabValue === 0}
                  onClick={(e) => handleTabChange(e, 0)}
                >
                  <ListItemIcon>
                    <ShoppingBagIcon color={tabValue === 0 ? "primary" : "inherit"} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t.sidebar.orders} 
                    primaryTypographyProps={{ 
                      fontWeight: tabValue === 0 ? 600 : 400 
                    }}
                  />
                  <Badge badgeContent={recentOrders.length} color="primary" />
                </StyledListItem>
                
                <StyledListItem 
                  button 
                  selected={tabValue === 1}
                  onClick={(e) => handleTabChange(e, 1)}
                >
                  <ListItemIcon>
                    <FavoriteIcon color={tabValue === 1 ? "primary" : "inherit"} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t.sidebar.wishlist} 
                    primaryTypographyProps={{ 
                      fontWeight: tabValue === 1 ? 600 : 400 
                    }}
                  />
                  <Badge badgeContent={wishlistItems.length} color="primary" />
                </StyledListItem>
                
                <StyledListItem 
                  button 
                  selected={tabValue === 2}
                  onClick={(e) => handleTabChange(e, 2)}
                >
                  <ListItemIcon>
                    <LocationIcon color={tabValue === 2 ? "primary" : "inherit"} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t.sidebar.addresses} 
                    primaryTypographyProps={{ 
                      fontWeight: tabValue === 2 ? 600 : 400 
                    }}
                  />
                </StyledListItem>
                
                <StyledListItem 
                  button 
                  selected={tabValue === 3}
                  onClick={(e) => handleTabChange(e, 3)}
                >
                  <ListItemIcon>
                    <CreditCardIcon color={tabValue === 3 ? "primary" : "inherit"} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t.sidebar.payments} 
                    primaryTypographyProps={{ 
                      fontWeight: tabValue === 3 ? 600 : 400 
                    }}
                  />
                </StyledListItem>
              </List>
              
              <Box sx={{ p: 2 }}>
                <Button 
                  variant="contained" 
                  fullWidth 
                  sx={{ 
                    background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                    borderRadius: '4px',
                    py: 1.5,
                    fontWeight: 600
                  }}
                  component={Link}
                  to="/customer/dashboard/profile/edit"
                >
                  {t.sidebar.editProfile}
                </Button>
              </Box>
            </StyledPaper>
          </Grid>
          )}
          
          {/* Main Content */}
          <Grid item xs={12} md={isMobile ? 12 : 9}>
            <StyledPaper sx={{ p: 0, overflow: 'hidden' }}>
              {/* Orders Tab */}
              {tabValue === 0 && (
                <Box>
                  <Box sx={{ 
                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                    color: 'white',
                    p: { xs: 2, sm: 3 }
                  }}>
                    <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 600, mb: 1 }}>
                      {t.orders.header}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {t.orders.subheader}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ p: { xs: 2, sm: 3 } }}>
                    {recentOrders.length > 0 ? (
                      <Grid container spacing={{ xs: 2, sm: 3 }}>
                        {recentOrders.map((order) => (
                          <Grid item xs={12} key={order.id}>
                            <StyledCard>
                              <CardContent>
                                {/* Desktop Layout */}
                                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                                  <Grid container spacing={3} alignItems="center">
                                    <Grid item xs={12} sm={3}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Avatar sx={{ 
                                          bgcolor: 'primary.main',
                                          width: 56,
                                          height: 56
                                        }}>
                                          <ShoppingBagIcon />
                                        </Avatar>
                                        <Box>
                                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {t.labels.order} #{order.id}
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            {order.date}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={4}>
                                      <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                                        {order.store}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        {order.items} {t.labels.itemsSuffix}
                                      </Typography>
                                      {order.placedBySupport && (
                                        <Box sx={{ mt: 1 }}>
                                          <Chip 
                                            label={t.orders.support}
                                            size="small"
                                            icon={<SupportAgentIcon />}
                                            sx={{ 
                                              bgcolor: '#e3f2fd',
                                              color: '#1976d2',
                                              fontSize: '0.7rem',
                                              height: '20px',
                                              '& .MuiChip-icon': {
                                                fontSize: '12px'
                                              }
                                            }}
                                          />
                                        </Box>
                                      )}
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={2}>
                                      <Chip 
                                        label={
                                          order.status === 'Delivered' ? t.orders.status.delivered :
                                          order.status === 'Shipped' ? t.orders.status.shipped :
                                          order.status === 'Processing' ? t.orders.status.processing : t.orders.status.pending
                                        } 
                                        color={
                                          order.status === 'Delivered' ? 'success' :
                                          order.status === 'Shipped' ? 'info' :
                                          order.status === 'Processing' ? 'primary' : 'warning'
                                        }
                                        sx={{ fontWeight: 600 }}
                                      />
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={3}>
                                      <Box sx={{ textAlign: 'right' }}>
                                        <Typography variant="h6" color="primary" sx={{ fontWeight: 700, mb: 1 }}>
                                          ৳{order.amount}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                          <Button 
                                            variant="outlined" 
                                            size="small"
                                            sx={{ borderRadius: '4px' }}
                                            href={`https://grameendelivery.com/tracking/${order.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            startIcon={<LocalShippingIcon />}
                                          >
                                            {t.orders.tracking}
                                          </Button>
                                          <Button 
                                            variant="contained" 
                                            size="small"
                                            sx={{ borderRadius: '4px' }}
                                            onClick={() => handleOrderClick(order)}
                                          >
                                            {t.orders.viewDetails}
                                          </Button>
                                        </Box>
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </Box>

                                {/* Mobile Layout */}
                                <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                    <Avatar sx={{ 
                                      bgcolor: 'primary.main',
                                      width: 48,
                                      height: 48
                                    }}>
                                      <ShoppingBagIcon />
                                    </Avatar>
                                    <Box sx={{ flexGrow: 1 }}>
                                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        {t.labels.order} #{order.id}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        {order.date}
                                      </Typography>
                                    </Box>
                                    <Chip 
                                      label={
                                        order.status === 'Delivered' ? t.orders.status.delivered :
                                        order.status === 'Shipped' ? t.orders.status.shipped :
                                        order.status === 'Processing' ? t.orders.status.processing : t.orders.status.pending
                                      } 
                                      color={
                                        order.status === 'Delivered' ? 'success' :
                                        order.status === 'Shipped' ? 'info' :
                                        order.status === 'Processing' ? 'primary' : 'warning'
                                      }
                                      size="small"
                                      sx={{ fontWeight: 600 }}
                                    />
                                  </Box>
                                  
                                  <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                                    {order.store}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {order.items} {t.labels.itemsSuffix}
                                  </Typography>
                                  
                                  {order.placedBySupport && (
                                    <Box sx={{ mb: 2 }}>
                                      <Chip 
                                        label={t.orders.support}
                                        size="small"
                                        icon={<SupportAgentIcon />}
                                        sx={{ 
                                          bgcolor: '#e3f2fd',
                                          color: '#1976d2',
                                          fontSize: '0.7rem',
                                          height: '20px',
                                          '& .MuiChip-icon': {
                                            fontSize: '12px'
                                          }
                                        }}
                                      />
                                    </Box>
                                  )}
                                  
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
                                      ৳{order.amount}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      <Button 
                                        variant="outlined" 
                                        size="small"
                                        sx={{ borderRadius: '4px', fontSize: '0.75rem' }}
                                        href={`https://grameendelivery.com/tracking/${order.id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        startIcon={<LocalShippingIcon />}
                                      >
                                        {t.orders.tracking}
                                      </Button>
                                      <Button 
                                        variant="contained" 
                                        size="small"
                                        sx={{ borderRadius: '4px', fontSize: '0.75rem' }}
                                        onClick={() => handleOrderClick(order)}
                                      >
                                        {t.orders.viewDetails}
                                      </Button>
                                    </Box>
                                  </Box>
                                </Box>
                              </CardContent>
                            </StyledCard>
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 8 }}>
                        <HistoryIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                          {t.orders.emptyTitle}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" gutterBottom>
                          {t.orders.emptySubtitle}
                        </Typography>
                        <Button 
                          variant="contained" 
                          size="large"
                          sx={{ 
                            mt: 3,
                            borderRadius: '4px',
                            px: 4,
                            py: 1.5,
                            background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)'
                          }}
                          component={Link}
                          to="/bazars"
                        >
                          {t.orders.startShopping}
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {/* Wishlist Tab */}
              {tabValue === 1 && (
                <Box>
                  <Box sx={{ 
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    color: 'white',
                    p: 3
                  }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                      {t.wishlist.header}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {t.wishlist.subheader}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ p: 3 }}>
                    {wishlistItems.length > 0 ? (
                      <Grid container spacing={3}>
                        {wishlistItems.map((item) => (
                          <Grid item xs={12} sm={6} md={4} key={item.id}>
                            <StyledCard>
                              <CardMedia
                                component="img"
                                height="200"
                                image={item.image}
                                alt={item.name}
                                sx={{ objectFit: 'cover' }}
                              />
                              <CardContent>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                  {item.name}
                                </Typography>
                                
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                  <StoreIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                                  <Typography variant="body2" color="text.secondary">
                                    {item.store}
                                  </Typography>
                                </Box>
                                
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                  <StarIcon fontSize="small" sx={{ color: '#ffc107', mr: 0.5 }} />
                                  <Typography variant="body2" color="text.secondary">
                                    {item.rating}
                                  </Typography>
                                </Box>
                                
                                <Typography variant="h6" color="primary" sx={{ fontWeight: 700, mb: 2 }}>
                                  ৳{item.price}
                                </Typography>
                                
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Button 
                                    size="small" 
                                    variant="outlined"
                                    startIcon={<ShoppingBagIcon />}
                                    sx={{ flex: 1, borderRadius: '4px' }}
                                  >
                                    {t.wishlist.addToCart}
                                  </Button>
                                  <Button 
                                    size="small" 
                                    variant="contained"
                                    sx={{ flex: 1, borderRadius: '4px' }}
                                    component={Link}
                                    to={`/products/${item.id}`}
                                  >
                                    {t.wishlist.view}
                                  </Button>
                                </Box>
                              </CardContent>
                            </StyledCard>
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 8 }}>
                        <FavoriteIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                          {t.wishlist.emptyTitle}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" gutterBottom>
                          {t.wishlist.emptySubtitle}
                        </Typography>
                        <Button 
                          variant="contained" 
                          size="large"
                          sx={{ 
                            mt: 3,
                            borderRadius: '4px',
                            px: 4,
                            py: 1.5,
                            background: 'linear-gradient(45deg, #f093fb 30%, #f5576c 90%)'
                          }}
                          component={Link}
                          to="/bazars"
                        >
                          {t.wishlist.viewProducts}
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {/* Addresses Tab */}
              {tabValue === 2 && (
                <Box>
                  <Box sx={{ 
                    background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                    color: '#333',
                    p: 3,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                      {t.addresses.header}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        {t.addresses.subheader}
                      </Typography>
                    </Box>
                    <Button 
                      variant="contained"
                      onClick={handleAddressDialogOpen}
                      sx={{ 
                        borderRadius: '4px',
                        background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)'
                      }}
                    >
                      {t.addresses.addNew}
                    </Button>
                  </Box>
                  
                  <Box sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                      {addresses.map((address) => (
                        <Grid item xs={12} md={6} key={address.id}>
                          <StyledCard sx={{ 
                            border: address.isDefault ? '2px solid' : '1px solid',
                            borderColor: address.isDefault ? 'primary.main' : 'divider'
                          }}>
                            <CardContent>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <LocationIcon color="primary" />
                                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {address.type === 'Home' ? t.labels.home : t.labels.office}
                                  </Typography>
                                  {address.isDefault && (
                                    <Chip label={t.labels.default} size="small" color="primary" />
                                  )}
                                </Box>
                                <IconButton size="small">
                                  <MoreVertIcon />
                                </IconButton>
                              </Box>
                              
                              <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
                                {address.address}
                              </Typography>
                              
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                {t.labels.phone} {address.phone}
                              </Typography>
                              
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button 
                                  variant="outlined" 
                                  size="small" 
                                  sx={{ borderRadius: '4px' }}
                                  component={Link}
                                  to={`/customer/dashboard/addresses/edit/${address.id}`}
                                >
                                  {t.addresses.edit}
                                </Button>
                                {!address.isDefault && (
                                  <Button 
                                    variant="outlined" 
                                    size="small" 
                                    color="error"
                                    sx={{ borderRadius: '4px' }}
                                  >
                                    {t.addresses.delete}
                                  </Button>
                                )}
                              </Box>
                            </CardContent>
                          </StyledCard>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Box>
              )}

              {/* Payment Methods Tab */}
              {tabValue === 3 && (
                <Box>
                  <Box sx={{ 
                    background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
                    color: '#333',
                    p: 3,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                      {t.payments.header}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      {t.payments.subheader}
                      </Typography>
                    </Box>
                    <Button 
                      variant="contained"
                      onClick={handlePaymentDialogOpen}
                      sx={{ 
                        borderRadius: '4px',
                        background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)'
                      }}
                    >
                      {t.payments.addNew}
                    </Button>
                  </Box>
                  
                  <Box sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                      {paymentMethods.map((payment) => (
                        <Grid item xs={12} md={6} key={payment.id}>
                          <StyledCard sx={{ 
                            border: payment.isDefault ? '2px solid' : '1px solid',
                            borderColor: payment.isDefault ? 'primary.main' : 'divider'
                          }}>
                            <CardContent>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <CreditCardIcon color="primary" />
                                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {payment.type}
                                  </Typography>
                                  {payment.isDefault && (
                                    <Chip label={t.labels.default} size="small" color="primary" />
                                  )}
                                </Box>
                                <IconButton size="small">
                                  <MoreVertIcon />
                                </IconButton>
                              </Box>
                              
                              <Typography variant="h6" sx={{ mb: 1, letterSpacing: 2 }}>
                                {payment.number}
                              </Typography>
                              
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                {t.labels.expires}: {payment.expiry}
                              </Typography>
                              
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button 
                                  variant="outlined" 
                                  size="small" 
                                  sx={{ borderRadius: '4px' }}
                                  component={Link}
                                  to={`/customer/dashboard/payment/edit/${payment.id}`}
                                >
                                  {t.addresses.edit}
                                </Button>
                                {!payment.isDefault && (
                                  <Button 
                                    variant="outlined" 
                                    size="small" 
                                    color="error"
                                    sx={{ borderRadius: '4px' }}
                                  >
                                    {t.addresses.delete}
                                  </Button>
                                )}
                              </Box>
                            </CardContent>
                          </StyledCard>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Box>
              )}
            </StyledPaper>
          </Grid>
        </Grid>
        
        {/* Recommended Products Section */}
        <Box sx={{ mt: 6 }}>
          <StyledPaper sx={{ p: 0, overflow: 'hidden' }}>
            <Box sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              p: 3
            }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                {t.recommended.header}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {t.recommended.subheader}
              </Typography>
            </Box>
            
            <Box sx={{ p: 3 }}>
              <Grid container spacing={3}>
                {[1, 2, 3, 4].map((item) => (
                  <Grid item xs={12} sm={6} md={3} key={item}>
                    <StyledCard>
                      <CardMedia
                        component="img"
                        height="180"
                        image={`https://source.unsplash.com/random/300x200?food&sig=${item}`}
                        alt={`Recommended product ${item}`}
                      />
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          {t.recommended.product(item)}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <StoreIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {t.recommended.storeName}
                          </Typography>
                        </Box>
                        
                        <Typography variant="h6" color="primary" sx={{ fontWeight: 700, mb: 2 }}>
                          ৳{120 + (item * 10)}
                        </Typography>
                        
                        <Button 
                          size="small" 
                          variant="contained" 
                          fullWidth
                          startIcon={<ShoppingBagIcon />}
                          sx={{ borderRadius: '4px' }}
                        >
                          {t.wishlist.addToCart}
                        </Button>
                      </CardContent>
                    </StyledCard>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </StyledPaper>
        </Box>
      </Container>
      
      {/* Add Address Dialog */}
      <Dialog 
        open={openAddressDialog} 
        onClose={handleAddressDialogClose}
        maxWidth="sm"
        fullWidth
        disableScrollLock={true}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            {t.dialogs.address.title}
            <IconButton onClick={handleAddressDialogClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>{t.dialogs.address.type}</InputLabel>
              <Select
                value={newAddress.type}
                label={t.dialogs.address.type}
                onChange={(e) => setNewAddress({ ...newAddress, type: e.target.value })}
              >
                <MenuItem value="Home">{t.dialogs.address.types.home}</MenuItem>
                <MenuItem value="Office">{t.dialogs.address.types.office}</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label={t.dialogs.address.detail}
              multiline
              rows={3}
              value={newAddress.address}
              onChange={(e) => setNewAddress({ ...newAddress, address: e.target.value })}
              fullWidth
            />
            
            <TextField
              label={t.dialogs.address.phone}
              value={newAddress.phone}
              onChange={(e) => setNewAddress({ ...newAddress, phone: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddressDialogClose}>{t.dialogs.address.cancel}</Button>
          <Button variant="contained" onClick={handleAddressSubmit}>{t.dialogs.address.save}</Button>
        </DialogActions>
      </Dialog>

      {/* Add Payment Method Dialog */}
      <Dialog 
        open={openPaymentDialog} 
        onClose={handlePaymentDialogClose}
        maxWidth="sm"
        fullWidth
        disableScrollLock={true}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            {t.dialogs.payment.title}
            <IconButton onClick={handlePaymentDialogClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>{t.dialogs.payment.type}</InputLabel>
              <Select
                value={newPayment.type}
                label={t.dialogs.payment.type}
                onChange={(e) => setNewPayment({ ...newPayment, type: e.target.value })}
              >
                <MenuItem value="Credit Card">{t.dialogs.payment.types.card}</MenuItem>
                <MenuItem value="Bkash">{t.dialogs.payment.types.bkash}</MenuItem>
                <MenuItem value="Nagad">{t.dialogs.payment.types.nagad}</MenuItem>
                <MenuItem value="Rocket">{t.dialogs.payment.types.rocket}</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label={newPayment.type === 'Credit Card' ? t.dialogs.payment.numberCard : t.dialogs.payment.numberAccount}
              value={newPayment.number}
              onChange={(e) => setNewPayment({ ...newPayment, number: e.target.value })}
              fullWidth
            />
            
            {newPayment.type === 'Credit Card' && (
              <TextField
                label={t.dialogs.payment.expiry}
                value={newPayment.expiry}
                onChange={(e) => setNewPayment({ ...newPayment, expiry: e.target.value })}
                placeholder="MM/YY"
                fullWidth
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePaymentDialogClose}>{t.dialogs.payment.cancel}</Button>
          <Button variant="contained" onClick={handlePaymentSubmit}>{t.dialogs.payment.save}</Button>
        </DialogActions>
      </Dialog>

      {/* Order Details Dialog */}
      <Dialog 
        open={openOrderDialog} 
        onClose={() => setOpenOrderDialog(false)}
        maxWidth="md"
        fullWidth
        disableScrollLock={true}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6">{t.dialogs.order.title}</Typography>
              <Typography variant="subtitle2" color="text.secondary">
                {t.dialogs.order.orderId(selectedOrder?.id || '')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip 
                label={
                  selectedOrder?.status === 'Delivered' ? 'ডেলিভারি হয়েছে' :
                  selectedOrder?.status === 'Shipped' ? 'শিপ করা হয়েছে' :
                  selectedOrder?.status === 'Processing' ? 'প্রক্রিয়াধীন' : 'অপেক্ষমান'
                } 
                color={
                  selectedOrder?.status === 'Delivered' ? 'success' :
                  selectedOrder?.status === 'Shipped' ? 'info' :
                  selectedOrder?.status === 'Processing' ? 'primary' : 'warning'
                }
                sx={{ fontWeight: 600 }}
              />
              <IconButton onClick={() => setOpenOrderDialog(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedOrder && (
            <Grid container spacing={3}>
              {/* Order Information */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ShoppingBagIcon color="primary" />
                      {t.dialogs.order.orderInfo}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, mr: 1 }}>
                        {t.labels.orderDate}
                      </Typography>
                      <Typography variant="body2">
                        {selectedOrder.date}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <StoreIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, mr: 1 }}>
                        {t.labels.store}
                      </Typography>
                      <Typography variant="body2">
                        {selectedOrder.store}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, mr: 1 }}>
                        {t.labels.totalItems}
                      </Typography>
                      <Typography variant="body2">
                        {selectedOrder.items} টি
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, mr: 1 }}>
                        {t.labels.totalAmount}
                      </Typography>
                      <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
                        ৳{selectedOrder.amount}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Delivery Information */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocalShippingIcon color="primary" />
                      {t.dialogs.order.deliveryInfo}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                        অবস্থা:
                      </Typography>
                      <Chip 
                         label={
                           selectedOrder.status === 'Delivered' ? t.orders.status.delivered :
                           selectedOrder.status === 'Shipped' ? t.orders.status.shipped :
                           selectedOrder.status === 'Processing' ? t.orders.status.processing : t.orders.status.pending
                         } 
                        color={
                          selectedOrder.status === 'Delivered' ? 'success' :
                          selectedOrder.status === 'Shipped' ? 'info' :
                          selectedOrder.status === 'Processing' ? 'primary' : 'warning'
                        }
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                    
                    <Box sx={{ mb: 2 }}>
                      <Button 
                        variant="outlined" 
                        fullWidth
                        startIcon={<LocalShippingIcon />}
                        href={`https://grameendelivery.com/tracking/${selectedOrder.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ borderRadius: '4px' }}
                      >
                        {t.dialogs.order.tracking}
                      </Button>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                      {t.dialogs.order.trackingNote}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Order Summary */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {t.dialogs.order.summary}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: '4px' }}>
                      <Typography variant="body1" paragraph>
                        <strong>{t.labels.orderNumber}</strong> #{selectedOrder.id}
                      </Typography>
                      <Typography variant="body1" paragraph>
                        <strong>{t.labels.orderDate}</strong> {selectedOrder.date}
                      </Typography>
                      <Typography variant="body1" paragraph>
                        <strong>{t.labels.store}</strong> {selectedOrder.store}
                      </Typography>
                      <Typography variant="body1" paragraph>
                        <strong>{t.labels.totalItems}</strong> {selectedOrder.items} {t.labels.itemsSuffix}
                      </Typography>
                      <Typography variant="body1">
                        <strong>{t.labels.totalAmount}</strong> <span style={{ color: '#1976d2', fontWeight: 700 }}>৳{selectedOrder.amount}</span>
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            variant="outlined" 
            startIcon={<LocalShippingIcon />}
            href={`https://grameendelivery.com/tracking/${selectedOrder?.id}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ borderRadius: '4px' }}
          >
            {t.dialogs.order.tracking}
          </Button>
          <Button onClick={() => setOpenOrderDialog(false)} variant="contained" sx={{ borderRadius: '4px' }}>
            {t.dialogs.order.close}
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </>
  );
};

export default CustomerDashboard;
